<template>
  <main class="pb-6 max-w-3xl">

    <!-- Loading State -->
    <div v-if="isLoading && !forecast"
         class="flex flex-col items-center justify-center h-[calc(100vh-100px)] text-(--color-secondary)">
      <md-circular-progress indeterminate></md-circular-progress>
      <div class="text-center p-8 text-lg">
        {{ loadingPhase === 'location' ? $t('home.findingLocation') : $t('home.loadingData') }}
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error"
         class="flex flex-col items-center justify-center h-[calc(100vh-100px)] text-secondary">
      <div class="text-center p-8 font-medium max-w-lg">
        <LocationOffOutline class="size-24 mx-auto mb-8" v-if="error.type === 'geolocation'"/>
        <WeatherHailOutline class="size-24 mx-auto mb-8" v-else/>
        <p v-if="error.type === 'network'">{{ $t('home.offlineError') }}</p>
        <p v-else-if="error.type === 'provider'">
          {{ $t('home.providerError', { providerName: providerDisplayName }) }}
        </p>
        <p v-else-if="error.type === 'forecast'">
          {{ $t('home.forecastUnavailableError') }}
        </p>
        <p v-else-if="error.type === 'server'">
          {{ $t('home.serverError') }}
          <!-- User-friendly debug info -->
          <span class="block text-xs font-normal text-gray-500 mt-2">
            {{ error.debugInfo }}
          </span>
        </p>
        <p v-else>{{ error.message }}</p>
      </div>
      <md-filled-button @click="retryLoadForecast" class="px-6">{{ $t('home.retry') }}</md-filled-button>
    </div>

    <!-- Success State -->
    <div v-else-if="forecast">
      <!-- (Rest of the template is unchanged) -->
      <template v-if="showMinutelyData">
        <h3>{{ $t('home.upcomingRain') }}</h3>
        <UpcomingRain :minutely="forecast.minutely"/>
      </template>
      <h3>{{ $t('home.currently') }}</h3>
      <CurrentWeather :daily="forecast.daily"/>
      <h3>{{ $t('home.thisWeek') }}</h3>
      <WeeklyForecast :daily="forecast.daily" :timezone="forecast.timezone"/>
      <div class="flex items-center justify-between lg:px-4 pt-4">
        <AppSelect
            settingKey="provider"
            id="provider-select"
            :header="$t('settings.data_source')"
            class="!text-xs bg-transparent !rounded-full !m-0 ring-hover hover:ring-8 !py-0 px-2 !border-none"
        >
          <template #display>
            <span v-if="forecast.provider !== 'appleweather'">{{ formattedDisplayProvider }}</span>
            <i18n-t v-else :keypath="formattedDisplayProvider" tag="span" class="flex items-center gap-1">
              <template #appleIcon>
                <AppleIcon class="size-3 -translate-y-px" />
              </template>
            </i18n-t>
          </template>
        </AppSelect>
        <ForecastInfo :forecast="forecast"/>
      </div>
    </div>
  </main>
</template>
<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue';
import * as Sentry from "@sentry/vue";
import {useRoute} from 'vue-router';
import {useI18n} from 'vue-i18n';

// Component & Store Imports
import CurrentWeather from '../components/CurrentWeather.vue';
import AppSelect from '../components/AppSelect.vue';
import UpcomingRain from '../components/UpcomingRain.vue';
import WeeklyForecast from '../components/WeeklyForecast.vue';
import ForecastInfo from '../components/ForecastInfo.vue';
import {useWeatherDisplayStore} from '../stores/weatherDisplay';
import {useSettingsStore} from '../stores/settings';
import {startForecastRefresh} from '../utils/forecastRefresh.mjs';
import {createDeviceLocator} from '../utils/deviceLocation.mjs';

// Icon Imports
import WeatherHailOutline from '~icons/material-symbols/weather-hail-outline';
import LocationOffOutline from '~icons/material-symbols/location-off-outline';
import AppleIcon from '~icons/simple-icons/apple';

// Material Web Imports
import '@material/web/progress/circular-progress.js';
import '@material/web/button/filled-button.js';
import '@material/web/icon/icon.js';
import '@material/web/iconbutton/icon-button.js';

// --- Constants & Store Instances ---
const route = useRoute();
const {t, locale} = useI18n();
const weatherDisplayStore = useWeatherDisplayStore();
const settingsStore = useSettingsStore();

// --- Reactive State ---
const forecast = ref(null);
const isLoading = ref(false);
const loadingPhase = ref('location');
const error = ref(null);

const showMinutelyData = computed(() => {
  const firstMinutelyTime = forecast.value?.minutely?.data?.[0]?.time;
  return typeof firstMinutelyTime === 'number' && firstMinutelyTime > (Date.now() / 1000 - 360);
});

// --- Computed Properties ---
const formattedDisplayProvider = computed(() => {
  const providerKey = forecast.value?.provider;
  if (!providerKey) return '';

  const providerMap = {
    metno: 'home.byMetNo',
    meteofrance: 'home.byMeteoFrance',
    appleweather: 'home.byAppleWeather',
    googleweather: 'home.byGoogleWeather',
  };

  if (providerKey === 'appleweather') {
    return providerMap[providerKey];
  }

  return providerMap[providerKey] ? t(providerMap[providerKey]) : '';
});


const providerDisplayName = computed(() => {
  if (error.value?.type === 'provider') {
    const providerKey = `settings.data_source_options.${error.value.provider_name}`;
    return t(providerKey, error.value.provider_name);
  }
  return '';
});


// --- Helper Functions ---

let currentAbortController = null;
let currentForecastContext = null;
let stopForecastRefresh;
const deviceLocator = createDeviceLocator({
  getCached: () => weatherDisplayStore.deviceLocation,
  setCached: location => weatherDisplayStore.setDeviceLocation(location),
});

// --- API Fetching Logic ---

/**
 * Fetches the weather forecast. Instead of throwing errors, it returns an object
 * with either a `data` key on success or an `error` key on failure.
 * This simplifies error handling in the calling function.
 * @param {number} latitude
 * @param {number} longitude
 * @param {AbortSignal} signal
 * @returns {Promise<{data: object}|{error: object}>}
 */
async function fetchForecast(latitude, longitude, signal) {
  const provider = settingsStore.provider;
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  // Use new endpoint structure: /forecast?lat=...&lon=...&provider=...&lang=...
  const url = `${baseUrl}?provider=${provider || 'auto'}&lat=${Number(latitude).toFixed(3)}&lon=${Number(longitude).toFixed(3)}&lang=${locale.value}`;

  try {
    const response = await fetch(url, { signal, cache: 'no-store' });

    if (!response.ok) {
      const errorBody = await response.text();
      let errorJson = {};
      try {
        errorJson = JSON.parse(errorBody);
      } catch (e) {
        // Not a json error, that's fine
      }

      if (response.status === 502 && errorJson.error === 'provider_unavailable') {
        return {error: {type: 'provider', provider_name: errorJson.provider_name}};
      }

      if (response.status === 502 && errorJson.error === 'forecast_unavailable') {
        return {error: {type: 'forecast', message: t('home.forecastUnavailableError')}};
      }

      const message = errorJson.error || `Request failed`;
      const debugInfo = `Status ${response.status} - ${message}`;
      Sentry.captureMessage(debugInfo);
      return {error: {type: 'server', message: t('home.serverError'), debugInfo}};
    }

    try {
      const data = await response.json();
      return {data};
    } catch (err) {
      Sentry.captureException(err);
      return {error: {type: 'server', message: t('home.serverError'), debugInfo: err.message}};
    }
  } catch (err) {
    if (err.name === 'AbortError') throw err; // Re-throw abort errors
    return {error: {type: 'network', message: t('home.offlineError')}};
  }
}

function prepareForecast(data) {
  const currentSeconds = Math.floor(Date.now() / 1000);

  if (!data || !Array.isArray(data.daily)) {
    return null;
  }

  const daily = data.daily.filter(day => {
    const dayStartTime = Number(day?.time);
    if (!Number.isFinite(dayStartTime)) return false;

    const dayEndTimeSeconds = dayStartTime + (24 * 60 * 60);
    return dayEndTimeSeconds >= currentSeconds;
  });

  if (daily.length === 0) {
    return null;
  }

  return {...data, daily};
}

// --- Forecast Loading ---

async function loadWeather({forcePrompt = false} = {}) {
  currentAbortController?.abort();
  const controller = new AbortController();
  currentAbortController = controller;
  const {signal} = controller;
  const context = JSON.stringify([route.name, route.params.lat, route.params.lng, settingsStore.provider, locale.value]);
  if (context !== currentForecastContext) forecast.value = null;
  currentForecastContext = context;
  isLoading.value = true;
  loadingPhase.value = 'location';
  error.value = null;

  try {
    const coords = route.name === 'LocationDetail'
      ? {lat: Number(route.params.lat), lon: Number(route.params.lng)}
      : await deviceLocator.get(forcePrompt);
    if (signal.aborted) return;

    if (route.name === 'LocationDetail') {
      weatherDisplayStore.setCurrentLocation({...coords, name: route.query.name});
    }
    if (forecast.value && (Number(forecast.value.latitude).toFixed(3) !== Number(coords.lat).toFixed(3) ||
        Number(forecast.value.longitude).toFixed(3) !== Number(coords.lon).toFixed(3))) {
      forecast.value = null;
    }

    loadingPhase.value = 'weather';
    const result = await fetchForecast(coords.lat, coords.lon, signal);
    if (signal.aborted) return;
    if (result.error) {
      error.value = result.error;
      return;
    }

    Sentry.setContext('weather_data', result.data);
    const prepared = prepareForecast(result.data);
    if (!prepared) {
      error.value = {type: 'forecast', message: t('home.forecastUnavailableError')};
      return;
    }

    forecast.value = prepared;
    weatherDisplayStore.setCurrentLocation({
      lat: Number(coords.lat).toFixed(5),
      lon: Number(coords.lon).toFixed(5),
      name: route.query.name || prepared.geoname || t('home.currentLocation'),
      country: prepared.country,
      admin1: prepared.admin1,
      admin2: prepared.admin2,
      timezone: prepared.timezone,
    });
  } catch (cause) {
    if (signal.aborted || cause.name === 'AbortError') return;
    if (loadingPhase.value === 'location') {
      const messages = {
        1: 'home.geolocationPermissionDenied',
        2: 'home.geolocationPositionUnavailable',
        3: 'home.geolocationTimeout',
        UNSUPPORTED: 'home.geolocationUnsupported',
        PROMPT_REQUIRED: 'home.geolocationPromptRequired',
      };
      if (!messages[cause.code]) Sentry.captureException(cause);
      error.value = {type: 'geolocation', message: t(messages[cause.code] || 'home.geolocationErrorGeneric')};
    } else {
      Sentry.captureException(cause);
      error.value = {type: 'server', message: t('home.serverError'), debugInfo: cause.message};
    }
  } finally {
    if (currentAbortController === controller) {
      isLoading.value = false;
      currentAbortController = null;
    }
  }
}

function retryLoadForecast() {
  loadWeather({forcePrompt: true});
}

function handleProviderChange(event) {
  if (event.key === 'provider') settingsStore.setSetting('provider', event.newValue ?? 'auto');
}

// --- Watchers & Lifecycle Hooks ---

watch(
    [() => route.fullPath, () => settingsStore.provider, locale],
    () => loadWeather(),
    {immediate: true}
);

onMounted(() => {
  stopForecastRefresh = startForecastRefresh({
    refresh: loadWeather,
    isLoading: () => isLoading.value,
  });
  window.addEventListener('storage', handleProviderChange);
});

onUnmounted(() => {
  currentAbortController?.abort();
  deviceLocator.stop();
  stopForecastRefresh?.();
  window.removeEventListener('storage', handleProviderChange);
});
</script>
